import { cva, VariantProps } from 'class-variance-authority';
import { ButtonHTMLAttributes, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

const styles = cva(
  [
    // base
    'inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg px-4 py-3 label-md whitespace-nowrap [text-transform:var(--button-text-transform,var(--label-text-transform,none))]',
    // focused
    'focus-visible:outline-(length:--state-focused-outline-width) focus-visible:outline-offset-(--state-focused-outline-offset) focus-visible:outline-(color:--state-focused-outline-color)',
    // hovered
    'hover:scale-102',
    // active
    'active:scale-98',
    // disabled
    'disabled:pointer-events-none',
  ],
  {
    variants: {
      variant: {
        filled: [
          // enabled
          'bg-brand text-on-brand',
          // focused
          'focus-visible:bg-mix focus-visible:mix-with-on-brand/(--state-focused-mix) focus-visible:mix-brand',
          // hovered
          'hover:bg-mix hover:mix-with-on-brand/(--state-hovered-mix) hover:mix-brand',
          // active
          'active:bg-mix active:mix-with-on-brand/(--state-active-mix) active:mix-brand',
          // disabled
          'disabled:bg-on-surface/(--state-disabled-mix) disabled:text-on-surface/(--state-disabled-mix)',
        ],
        outlined: [
          // enabled
          'border border-outline-variant bg-transparent text-on-surface-variant',
          // focused
          'focus-visible:bg-on-surface-variant/(--state-focused-mix)',
          // hovered
          'hover:bg-on-surface-variant/(--state-hovered-mix)',
          // active
          'active:bg-on-surface-variant/(--state-active-mix)',
          // disabled
          'disabled:bg-on-surface/(--state-disabled-mix) disabled:text-on-surface/(--state-disabled-mix)',
        ],
        ghost: [
          // enabled
          'bg-transparent text-brand',
          // focused
          'focus-visible:bg-brand/(--state-focused-mix)',
          // hovered
          'hover:bg-brand/(--state-hovered-mix)',
          // active
          'active:bg-brand/(--state-active-mix)',
          // disabled
          'disabled:text-on-surface/(--state-disabled-mix)',
        ],
      },
      pressed: { true: null }, // variant only present as part of a compound variant
      pending: {
        true: [
          'relative',
          'before:pointer-events-none before:absolute before:inset-0 before:rounded-[inherit] before:bg-surface-container-highest',
          'after:pointer-events-none after:absolute after:aspect-square after:h-[calc(100%-2*--spacing(2.5))] after:animate-spin after:rounded-full after:border-3 after:border-surface-container-lowest after:border-t-plain',
        ],
      },
    },
    compoundVariants: [
      // pressed compounds
      {
        variant: 'filled',
        pressed: false,
        class: [
          // enabled
          'bg-surface-container text-on-surface-variant',
          // focused
          'focus-visible:bg-mix focus-visible:mix-with-on-surface-variant/(--state-focused-mix) focus-visible:mix-surface-container',
          //  hovered
          'hover:bg-mix hover:mix-with-on-surface-variant/(--state-hovered-mix) hover:mix-surface-container',
          //  active
          'active:bg-mix active:mix-with-on-surface-variant/(--state-active-mix) active:mix-surface-container',
        ],
      },
      {
        variant: 'outlined',
        pressed: true,
        class: [
          // enabled
          'border-transparent bg-inverse-surface text-inverse-on-surface',
          // focused
          'focus-visible:bg-mix focus-visible:mix-with-inverse-on-surface/(--state-focused-mix) focus-visible:mix-inverse-surface',
          //  hovered
          'hover:bg-mix hover:mix-with-inverse-on-surface/(--state-hovered-mix) hover:mix-inverse-surface',
          //  active
          'active:bg-mix active:mix-with-inverse-on-surface/(--state-active-mix) active:mix-inverse-surface',
        ],
      },
    ],
  },
);

type Variance = VariantProps<typeof styles>;

export type ButtonProps = {
  /**
   * The color variant of the button.
   */
  variant?: Variance['variant'];
  /**
   * Controls the toggle state of the button.
   * When `true`, the button is in a pressed state. When `false`, it is not pressed. When `undefined`, the button is neither pressed nor unpressed.
   * Note: only applies to Filled,  and Outlined variants. Text buttons do not have a pressed state.
   */
  'aria-pressed'?: Variance['pressed'];
  /**
   * Controls whether the button is in a pending state, such as during form submission.
   */
  'aria-busy'?: Variance['pending'];
} & ButtonHTMLAttributes<HTMLButtonElement>;

/**
 * Button component for support for different button styles and sizes on top of a base button element.
 *
 * @example
 * <Button variant="outlined">Outlined Button</Button>
 *
 * Additionally support `aria-pressed` for toggle buttons and `aria-busy` for pending state. When the button is disabled, these attributes will be ignored to prevent confusion for assistive technologies.
 *
 * @example
 * <Button variant="filled"  aria-pressed={isToggled} onClick={handleToggle}>
 *   Toggle Button
 * </Button>
 *
 * <Button  type="submit" variant="outlined"  aria-busy={isLoading}>
 *  Submit
 * </Button>
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'filled',
      disabled,
      'aria-pressed': pressed,
      'aria-busy': pending,
      type = 'button',
      className,
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      // oxlint-disable-next-line react/button-has-type
      type={type}
      className={twMerge(styles({ variant, pending, pressed }), className)}
      disabled={disabled || !!pending}
      aria-busy={!disabled ? pending : undefined}
      aria-pressed={!disabled ? pressed : undefined}
      data-component="corso-button"
      data-variant={variant}
      {...props}
    />
  ),
);
Button.displayName = 'Button';

export default Button;
